import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../supabase";

export default function ReferenceVerification() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading, notFound, pending, confirmed, alreadyConfirmed, error
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (token) {
      loadProfile();
    } else {
      setStatus("notFound");
    }
  }, [token]);

  const loadProfile = async () => {
    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .select(
          "full_name, bio1, location, church_name, church_city, referee_name, reference_status"
        )
        .eq("reference_token", token)
        .single();

      if (error || !data) {
        setStatus("notFound");
        return;
      }

      setProfile(data);

      if (data.reference_status === "confirmed") {
        setStatus("alreadyConfirmed");
      } else {
        setStatus("pending");
      }
    } catch (err) {
      console.error("Error loading profile:", err);
      setStatus("error");
    }
  };

  const handleConfirm = async () => {
    setStatus("loading");
    try {
      const { error } = await supabase
        .from("user_profiles")
        .update({
          reference_status: "confirmed",
          profile_visible: true,
        })
        .eq("reference_token", token);

      if (error) {
        console.error("Error confirming reference:", error);
        setStatus("error");
        return;
      }

      setStatus("confirmed");
    } catch (err) {
      console.error("Error confirming reference:", err);
      setStatus("error");
    }
  };

  const containerStyle = {
    minHeight: "100vh",
    backgroundColor: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "40px 32px",
    maxWidth: "480px",
    width: "100%",
    boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06)",
    border: "1px solid #e5e5e5",
    textAlign: "center",
  };

  if (status === "loading") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "3px solid #e5e5e5",
              borderTop: "3px solid #1a1a1a",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p style={{ color: "#737373", fontSize: "16px", margin: 0 }}>
            Loading...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (status === "notFound") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div
            style={{
              width: "64px",
              height: "64px",
              backgroundColor: "#fef2f2",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#dc2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h1
            style={{
              fontSize: "22px",
              fontWeight: "700",
              color: "#1a1a1a",
              marginBottom: "8px",
            }}
          >
            Link not found
          </h1>
          <p style={{ color: "#737373", fontSize: "15px", margin: 0 }}>
            This verification link is invalid or has expired.
          </p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h1
            style={{
              fontSize: "22px",
              fontWeight: "700",
              color: "#1a1a1a",
              marginBottom: "8px",
            }}
          >
            Something went wrong
          </h1>
          <p style={{ color: "#737373", fontSize: "15px", margin: 0 }}>
            Please try again later or contact support.
          </p>
        </div>
      </div>
    );
  }

  if (status === "alreadyConfirmed") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div
            style={{
              width: "64px",
              height: "64px",
              backgroundColor: "#10b981",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1
            style={{
              fontSize: "22px",
              fontWeight: "700",
              color: "#1a1a1a",
              marginBottom: "8px",
            }}
          >
            Already confirmed
          </h1>
          <p style={{ color: "#737373", fontSize: "15px", margin: 0 }}>
            This reference has already been confirmed. Thank you for your support!
          </p>
        </div>
      </div>
    );
  }

  if (status === "confirmed") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div
            style={{
              width: "80px",
              height: "80px",
              backgroundColor: "#10b981",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
              boxShadow: "0 8px 32px rgba(16, 185, 129, 0.3)",
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#1a1a1a",
              marginBottom: "12px",
            }}
          >
            Reference confirmed!
          </h1>
          <p
            style={{
              color: "#737373",
              fontSize: "16px",
              lineHeight: "1.6",
              margin: "0 0 28px 0",
            }}
          >
            Thank you for confirming {profile?.full_name}'s reference.
            Their profile is now visible to churches on Collavo.
          </p>

          {/* Divider */}
          <div style={{ borderTop: "1px solid #e5e5e5", marginBottom: "24px" }} />

          {/* CTA */}
          <p
            style={{
              fontSize: "15px",
              fontWeight: "600",
              color: "#1a1a1a",
              marginBottom: "6px",
            }}
          >
            Discover what Collavo has to offer
          </p>
          <p
            style={{
              fontSize: "14px",
              color: "#737373",
              lineHeight: "1.5",
              marginBottom: "20px",
            }}
          >
            Browse speakers, musicians, and ministry professionals available to
            serve your church — or apply to share your own gifts.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <a
              href="/explore"
              style={{
                display: "block",
                width: "100%",
                padding: "13px 24px",
                fontSize: "15px",
                fontWeight: "600",
                color: "#fff",
                backgroundColor: "#1a1a1a",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                textDecoration: "none",
                textAlign: "center",
                boxSizing: "border-box",
              }}
            >
              Browse profiles
            </a>
            <a
              href="/"
              style={{
                display: "block",
                width: "100%",
                padding: "13px 24px",
                fontSize: "15px",
                fontWeight: "600",
                color: "#1a1a1a",
                backgroundColor: "#fff",
                border: "1px solid #e5e5e5",
                borderRadius: "8px",
                cursor: "pointer",
                textDecoration: "none",
                textAlign: "center",
                boxSizing: "border-box",
              }}
            >
              Create an account
            </a>
          </div>
        </div>
      </div>
    );
  }

  // status === "pending" — show the confirmation form
  return (
    <div style={containerStyle}>
      <div style={{ ...cardStyle, textAlign: "left" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "700",
              color: "#1a1a1a",
              marginBottom: "16px",
            }}
          >
            Collavo
          </div>
          <h1
            style={{
              fontSize: "22px",
              fontWeight: "700",
              color: "#1a1a1a",
              marginBottom: "8px",
            }}
          >
            Church Reference Request
          </h1>
          <p style={{ color: "#737373", fontSize: "15px", margin: 0 }}>
            Hi {profile?.referee_name}, you've been listed as a church reference.
          </p>
        </div>

        {/* Applicant Info */}
        <div
          style={{
            backgroundColor: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h3
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "12px",
            }}
          >
            Applicant Details
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div>
              <span style={{ fontSize: "13px", color: "#94a3b8" }}>Name</span>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1a1a1a",
                  margin: "2px 0 0 0",
                }}
              >
                {profile?.full_name || "—"}
              </p>
            </div>
            {profile?.bio1 && (
              <div>
                <span style={{ fontSize: "13px", color: "#94a3b8" }}>Role</span>
                <p
                  style={{
                    fontSize: "15px",
                    color: "#334155",
                    margin: "2px 0 0 0",
                  }}
                >
                  {profile.bio1}
                </p>
              </div>
            )}
            {profile?.church_name && (
              <div>
                <span style={{ fontSize: "13px", color: "#94a3b8" }}>Church</span>
                <p
                  style={{
                    fontSize: "15px",
                    color: "#334155",
                    margin: "2px 0 0 0",
                  }}
                >
                  {profile.church_name}
                  {profile.church_city && `, ${profile.church_city}`}
                </p>
              </div>
            )}
            {profile?.location && (
              <div>
                <span style={{ fontSize: "13px", color: "#94a3b8" }}>
                  Location
                </span>
                <p
                  style={{
                    fontSize: "15px",
                    color: "#334155",
                    margin: "2px 0 0 0",
                  }}
                >
                  {profile.location}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Confirmation message */}
        <p
          style={{
            fontSize: "15px",
            color: "#525252",
            lineHeight: "1.6",
            marginBottom: "24px",
          }}
        >
          By confirming, you verify that you know{" "}
          <strong>{profile?.full_name}</strong> and that they are part of your
          church community. Their profile will become visible on Collavo.
        </p>

        {/* Confirm Button */}
        <button
          onClick={handleConfirm}
          style={{
            width: "100%",
            padding: "16px 24px",
            fontSize: "16px",
            fontWeight: "600",
            color: "#fff",
            backgroundColor: "#1a1a1a",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "background-color 0.2s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#333")}
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#1a1a1a")
          }
        >
          Confirm Reference
        </button>

        <p
          style={{
            fontSize: "13px",
            color: "#a3a3a3",
            textAlign: "center",
            marginTop: "16px",
            lineHeight: "1.5",
          }}
        >
          If you don't recognise this person or didn't expect this email, you can
          safely ignore this page.
        </p>
      </div>
    </div>
  );
}
